const MODE_STORAGE_KEY = 'awesomebible.readerPrefs.mode';
const FONT_STORAGE_KEY = 'awesomebible.readerPrefs.font';
const SIZE_STORAGE_KEY = 'awesomebible.readerPrefs.size';
const VALID_MODES = ['inline', 'verses', 'clean', 'refs'];
// Keep this list intentionally small for anonymous web readers.
// Future account tiers can expand from the Expo-derived set: Bitter, Cardo, EB Garamond, Georgia,
// Linguistics Pro, Noto Sans, Noto Serif, Roboto, Source Sans Pro, and Space Mono.
const VALID_FONTS = ['cardo', 'source-sans-pro'];
const VALID_SIZES = ['small', 'medium', 'large', 'x-large'];
const DEFAULT_MODE = 'inline';
const DEFAULT_FONT = 'cardo';
const DEFAULT_SIZE = 'medium';

function isValidMode(mode) {
    return VALID_MODES.indexOf(mode) !== -1;
}

function isValidFont(font) {
    return VALID_FONTS.indexOf(font) !== -1;
}

function isValidSize(size) {
    return VALID_SIZES.indexOf(size) !== -1;
}

function getStoredMode() {
    try {
        const mode = window.localStorage.getItem(MODE_STORAGE_KEY);
        return isValidMode(mode) ? mode : null;
    } catch (error) {
        return null;
    }
}

function getStoredFont() {
    try {
        const font = window.localStorage.getItem(FONT_STORAGE_KEY);
        return isValidFont(font) ? font : null;
    } catch (error) {
        return null;
    }
}

function getStoredSize() {
    try {
        const size = window.localStorage.getItem(SIZE_STORAGE_KEY);
        return isValidSize(size) ? size : null;
    } catch (error) {
        return null;
    }
}

function storeMode(mode) {
    try {
        window.localStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch (error) {
        // Ignore private browsing or disabled storage.
    }
}

function storeFont(font) {
    try {
        window.localStorage.setItem(FONT_STORAGE_KEY, font);
    } catch (error) {
        // Ignore private browsing or disabled storage.
    }
}

function storeSize(size) {
    try {
        window.localStorage.setItem(SIZE_STORAGE_KEY, size);
    } catch (error) {
        // Ignore private browsing or disabled storage.
    }
}

function getRoot(element) {
    if (!element) {
        return null;
    }
    if (element.closest) {
        return element.closest('[data-reader-prefs]');
    }
    return null;
}

function updateControls(root, mode) {
    const buttons = root.querySelectorAll('[data-reader-mode-button]');
    Array.prototype.forEach.call(buttons, (button) => {
        const selected = button.getAttribute('data-reader-mode-button') === mode;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
}

function updateFontControls(root, font) {
    const selects = root.querySelectorAll('[data-reader-font-select]');
    Array.prototype.forEach.call(selects, (select) => {
        select.value = font;
    });
}

function updateSizeControls(root, size) {
    const selects = root.querySelectorAll('[data-reader-size-select]');
    Array.prototype.forEach.call(selects, (select) => {
        select.value = size;
    });
}

function setMode(root, mode, options) {
    const shouldPersist = !options || options.persist !== false;
    const nextMode = isValidMode(mode) ? mode : DEFAULT_MODE;
    root.setAttribute('data-reader-mode', nextMode);
    updateControls(root, nextMode);
    if (shouldPersist) {
        storeMode(nextMode);
    }
}

function setFont(root, font, options) {
    const shouldPersist = !options || options.persist !== false;
    const nextFont = isValidFont(font) ? font : DEFAULT_FONT;
    root.setAttribute('data-reader-font', nextFont);
    updateFontControls(root, nextFont);
    if (shouldPersist) {
        storeFont(nextFont);
    }
}

function setSize(root, size, options) {
    const shouldPersist = !options || options.persist !== false;
    const nextSize = isValidSize(size) ? size : DEFAULT_SIZE;
    root.setAttribute('data-reader-size', nextSize);
    updateSizeControls(root, nextSize);
    if (shouldPersist) {
        storeSize(nextSize);
    }
}

function initRoot(root) {
    const storedMode = getStoredMode();
    const storedFont = getStoredFont();
    const storedSize = getStoredSize();
    const initialMode = storedMode || root.getAttribute('data-reader-mode') || DEFAULT_MODE;
    const initialFont = storedFont || root.getAttribute('data-reader-font') || DEFAULT_FONT;
    const initialSize = storedSize || root.getAttribute('data-reader-size') || DEFAULT_SIZE;
    setMode(root, initialMode, { persist: false });
    setFont(root, initialFont, { persist: false });
    setSize(root, initialSize, { persist: false });
    root.setAttribute('data-reader-prefs-ready', 'true');
}

function initAllRoots() {
    const roots = document.querySelectorAll('[data-reader-prefs]');
    Array.prototype.forEach.call(roots, initRoot);
}

function handleModeClick(event) {
    const button = event.target.closest ? event.target.closest('[data-reader-mode-button]') : null;
    if (!button) {
        return;
    }

    const root = getRoot(button);
    if (!root) {
        return;
    }

    setMode(root, button.getAttribute('data-reader-mode-button'));
}

function handleFontChange(event) {
    const select = event.target.closest ? event.target.closest('[data-reader-font-select]') : null;
    if (!select) {
        return;
    }

    const root = getRoot(select);
    if (!root) {
        return;
    }

    setFont(root, select.value);
}

function handleSizeChange(event) {
    const select = event.target.closest ? event.target.closest('[data-reader-size-select]') : null;
    if (!select) {
        return;
    }

    const root = getRoot(select);
    if (!root) {
        return;
    }

    setSize(root, select.value);
}

function observeDynamicContent() {
    if (!window.MutationObserver) {
        return;
    }

    const observer = new MutationObserver(() => initAllRoots());
    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });
}

function targetElementFromHash() {
    if (!window.location.hash || window.location.hash.indexOf('#verse-') !== 0) {
        return null;
    }

    try {
        return document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
    } catch (error) {
        return null;
    }
}

function focusHashVerse() {
    const target = targetElementFromHash();
    if (!target) {
        return;
    }

    if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
    }

    target.classList.remove('reader-verse-target-highlight');
    // Force the animation to restart if another verse hash is selected later.
    target.offsetWidth; // eslint-disable-line no-unused-expressions
    target.classList.add('reader-verse-target-highlight');
    target.focus({ preventScroll: true });

    window.setTimeout(() => {
        target.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    }, 80);
}

export function initReaderPrefs() {
    initAllRoots();
    focusHashVerse();
    document.addEventListener('click', handleModeClick);
    document.addEventListener('change', handleFontChange);
    document.addEventListener('change', handleSizeChange);
    window.addEventListener('hashchange', focusHashVerse);
    observeDynamicContent();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReaderPrefs);
} else {
    initReaderPrefs();
}
